import { sharedApi } from "./sharedApi";

import type {
  PronunciationRequest,
  PronunciationAssessment,
} from "@lingoshade/domain";

const uploads = new Map<
  string,
  { userId: string; request: PronunciationRequest }
>();
const sessionAudio = new Map<string, Uint8Array>();
let sessionGeneration = 0;

export function clearPronunciationSessionAudio(): void {
  sessionGeneration++;
  sessionAudio.clear();
}

/** Extract a PCM WAV word clip only from recordings retained in this page's memory. */
export function pronunciationSessionWordAudio(
  id: string,
  offsetMs: number,
  durationMs: number,
): Blob | null {
  const wav = sessionAudio.get(id);
  if (!wav || durationMs <= 0) return null;
  const header = new DataView(wav.buffer, wav.byteOffset, 44);
  const sampleRate = header.getUint32(24, true);
  const start = Math.max(0, Math.floor((offsetMs * sampleRate) / 1000)) * 2;
  const end = Math.min(
    wav.length - 44,
    Math.ceil(((offsetMs + durationMs) * sampleRate) / 1000) * 2,
  );
  if (start >= end) return null;
  const clip = new Uint8Array(44 + end - start);
  clip.set(wav.subarray(0, 44));
  clip.set(wav.subarray(44 + start, 44 + end), 44);
  const view = new DataView(clip.buffer);
  view.setUint32(4, clip.length - 8, true);
  view.setUint32(40, clip.length - 44, true);
  return new Blob([clip.buffer], { type: "audio/wav" });
}

/** Audio never enters Redux actions, devtools, local storage or saved history. */
export function preparePronunciationUpload(
  userId: string,
  request: PronunciationRequest,
): string {
  const id = crypto.randomUUID();
  uploads.set(id, { userId, request });
  return id;
}

export function clearPronunciationUploads(): void {
  clearPronunciationSessionAudio();
  for (const entry of uploads.values()) entry.request.audioBase64 = "";
  uploads.clear();
}

export async function sendPronunciationUpload(
  id: string,
  userId: string,
  token: string | null,
  timeoutMs?: number,
): Promise<PronunciationAssessment> {
  const entry = uploads.get(id);
  uploads.delete(id);
  if (!entry || entry.userId !== userId)
    throw new Error("PRONUNCIATION_CANCELLED");
  const generation = sessionGeneration;
  try {
    const result = await sharedApi.pronunciation.assess(
      entry.request,
      token,
      timeoutMs,
    );
    if (generation !== sessionGeneration) return result;
    sessionAudio.clear();
    const decoded = atob(entry.request.audioBase64);
    sessionAudio.set(
      result.id,
      Uint8Array.from(decoded, (character): number => character.charCodeAt(0)),
    );
    return result;
  } finally {
    entry.request.audioBase64 = "";
  }
}
